/* eslint-disable @typescript-eslint/ban-types */
import { FC, useState, ReactElement } from "react"
import { Controller, useForm } from "react-hook-form"
import {
  Challenge,
  ChallengesService,
  UpdateChallengeRequestSchema
} from "../sdk"
import { useParams } from "react-router-dom"
import classNames from "classnames"
import Col from "./Col"
import Input from "./Input"
import Textarea from "./Textarea"
import Button from "../components/Button"
import useError from "../hooks/useError"
import Checkbox from "./Checkbox"
import { toIsoString } from "../helpers/date"

type Props = {
  onSuccess?(challenge: Challenge): void
  defaultValues: Challenge
}

const EditChallengeForm: FC<Props> = ({
  onSuccess,
  defaultValues: {
    name,
    alias,
    description,
    startTimestamp,
    endTimestamp,
    startSubscriptionTimestamp,
    endSubscriptionTimestamp,
    maxPartecipants,
    difficulty,
    training,
    visibleTimestamp,
    private: challengePrivate
  }
}) => {
  const [loading, setLoading] = useState<boolean>(false)

  const { handleSubmit, control, formState, getValues } = useForm<UpdateChallengeRequestSchema["updates"]>()
  const { handleError } = useError()
  const { id } = useParams()

  const onSubmit = async({
    name,
    alias,
    description,
    startTimestamp,
    endTimestamp,
    startSubscriptionTimestamp,
    endSubscriptionTimestamp,
    difficulty,
    training,
    maxPartecipants
  }: UpdateChallengeRequestSchema["updates"]): Promise<void> => {
    setLoading(true)
    try {
      const { data } = await ChallengesService.updateChallenge({
        handler: { challengeId: Number(id) },
        updates: {
          name,
          alias,
          description,
          startTimestamp,
          endTimestamp,
          startSubscriptionTimestamp,
          endSubscriptionTimestamp,
          difficulty: Number(difficulty),
          training,
          maxPartecipants
        }
      })
      if (onSuccess) {
        onSuccess(data)
      }
    } catch (err) {
      handleError(err)
    }
    setLoading(false)
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={classNames("grid grid-cols-12 gap-3", loading && "opacity-50 pointer-events-none")}>
      <Col lg={6}>
        <Controller
          name="name"
          control={control}
          defaultValue={name}
          render={({ field: { onChange, value } }): ReactElement => {
            return (
              <Input
                value={value}
                onChange={onChange}
                error={formState.errors.name?.message}
                label="Titolo"
              />
            )
          }}
        />
      </Col>

      <Col lg={6}>
        <Controller
          name="alias"
          control={control}
          defaultValue={alias || ""}
          render={({ field: { onChange, value } }): ReactElement => {
            return (
              <Input
                value={value}
                onChange={onChange}
                error={formState.errors.alias?.message}
                label="Alias"
              />
            )
          }}
        />
      </Col>

      <Col>
        <Controller
          name="description"
          control={control}
          defaultValue={description}
          rules={{ required: "Campo obbligatorio" }}
          render={({ field: { onChange, value } }): ReactElement => {
            return (
              <Textarea
                value={value}
                onChange={onChange}
                error={formState.errors.description?.message}
                label="Descrizione"
              />
            )
          }}
        />
      </Col>

      {!training &&
        <>
          <Col lg={6}>
            <Controller
              name="visibleTimestamp"
              control={control}
              defaultValue={toIsoString(new Date(visibleTimestamp)) || ""}
              render={({ field: { onChange, value } }): ReactElement => {
                return (
                  <Input
                    type="datetime-local"
                    value={value!}
                    onChange={onChange}
                    error={formState.errors.visibleTimestamp?.message}
                    label="Data di pubblicazione"
                  />
                )
              }}
            />
          </Col>

          <Col lg={6} />

          <Col lg={6}>
            <Controller
              name="startSubscriptionTimestamp"
              control={control}
              defaultValue={toIsoString(new Date(startSubscriptionTimestamp)) || ""}
              rules={{ required: "Campo obbligatorio" }}
              render={({ field: { onChange, value } }): ReactElement => {
                return (
                  <Input
                    type="datetime-local"
                    value={value!}
                    onChange={onChange}
                    error={formState.errors.startSubscriptionTimestamp?.message}
                    label="Inizio iscrizioni"
                  />
                )
              }}
            />
          </Col>

          <Col lg={6}>
            <Controller
              name="endSubscriptionTimestamp"
              control={control}
              defaultValue={toIsoString(new Date(endSubscriptionTimestamp)) || ""}
              rules={{ required: "Campo obbligatorio" }}
              render={({ field: { onChange, value } }): ReactElement => {
                return (
                  <Input
                    type="datetime-local"
                    value={value!}
                    onChange={onChange}
                    error={formState.errors.endSubscriptionTimestamp?.message}
                    label="Fine iscrizioni"
                  />
                )
              }}
            />
          </Col>

          <Col lg={6}>
            <Controller
              name="startTimestamp"
              control={control}
              defaultValue={toIsoString(new Date(startTimestamp)) || ""}
              rules={{ required: "Campo obbligatorio" }}
              render={({ field: { onChange, value } }): ReactElement => {
                return (
                  <Input
                    type="datetime-local"
                    value={value!}
                    onChange={onChange}
                    error={formState.errors.startTimestamp?.message}
                    label="Inizio"
                  />
                )
              }}
            />
          </Col>

          <Col lg={6}>
            <Controller
              name="endTimestamp"
              control={control}
              defaultValue={toIsoString(new Date(endTimestamp)) || ""}
              rules={{ required: "Campo obbligatorio" }}
              render={({ field: { onChange, value } }): ReactElement => {
                return (
                  <Input
                    type="datetime-local"
                    value={value!}
                    onChange={onChange}
                    error={formState.errors.endTimestamp?.message}
                    label="Fine"
                  />
                )
              }}
            />
          </Col>
        </>
      }

      <Col lg={6}>
        <Controller
          name="maxPartecipants"
          defaultValue={maxPartecipants}
          rules={{
            validate: (value: number | undefined): boolean | string => {
              const v = +(value || 0)
              if (v <= 0) {
                return "Valore non valido"
              }
              if (getValues("individual")) {
                return v === 1 || "Nelle gare singole può esserci un solo partecipante"
              }
              return true
            }
          }}
          control={control}
          render={({ field: { onChange, value } }): ReactElement => {
            return (
              <Input value={value} type="number" onChange={onChange} label="Numero massimo di partecipanti" error={formState.errors.maxPartecipants?.message} />
            )
          }}
        />
      </Col>

      <Col lg={6}>
        <Controller
          name="difficulty"
          defaultValue={difficulty}
          rules={{
            validate: (value: number | undefined): boolean | string => {
              const v = +(value || 0)
              return (v >= 1 && v <= 5) || "Il valore deve essere compreso tra 1 e 5"
            }
          }}
          control={control}
          render={({ field: { onChange, value } }): ReactElement => {
            return (
              <Input
                value={value}
                onChange={onChange}
                label="Difficoltà"
                type="number"
                min="1"
                max="5"
                error={formState.errors.difficulty?.message}
              />
            )
          }}
        />
      </Col>

      <Col lg={6}>
        <Controller
          name="private"
          control={control}
          defaultValue={challengePrivate}
          render={({ field: { onChange, value } }): ReactElement => {
            return (
              <Checkbox
                checked={value || false}
                onChange={onChange}
                className="ml-4"
                containerClassName="w-36"
                label="Privata"
              />
            )
          }}
        />
      </Col>

      <Col lg={6}>
        <Controller
          name="training"
          control={control}
          defaultValue={training}
          render={({ field: { onChange, value } }): ReactElement => {
            return (
              <Checkbox
                checked={value || false}
                onChange={onChange}
                className="ml-4"
                containerClassName="w-36"
                label="Allenamento"
              />
            )
          }}
        />
      </Col>

      <Col>
        <Button
          className="w-full"
          type="submit"
        >Salva</Button>
      </Col>
    </form>
  )
}

export default EditChallengeForm
